import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { employeesAPI, tasksAPI } from '../services/api';

const TASK_COLORS = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
  '#10b981', '#06b6d4', '#f97316', '#6366f1',
  '#14b8a6', '#ef4444',
];
const getColor = (index) => TASK_COLORS[index % TASK_COLORS.length];

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};
const formatLongDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};
const addDays = (dateStr, days) => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};
const diffDays = (a, b) => {
  const da = new Date(a + 'T12:00:00');
  const db = new Date(b + 'T12:00:00');
  return Math.round((db - da) / (1000 * 60 * 60 * 24));
};
// Count Mon–Fri working days inclusive (mirrors backend)
const countWorkingDays = (a, b) => {
  let count = 0;
  const cur = new Date(a + 'T12:00:00');
  const end = new Date(b + 'T12:00:00');
  while (cur <= end) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};
// Adaptive tick generator — density and label format based on range length
// ── Month header spans (top ruler row) ──
// Returns each month that overlaps [start,end] with its left% and width%
const getMonthSpans = (start, end) => {
  const totalDays = diffDays(start, end) + 1;
  const spans = [];
  const cur = new Date(start + 'T12:00:00');
  const endD = new Date(end + 'T12:00:00');
  // Walk month by month
  const monthStart = new Date(cur); monthStart.setDate(1);
  while (monthStart <= endD) {
    const monthEnd = new Date(monthStart);
    monthEnd.setMonth(monthEnd.getMonth() + 1);
    monthEnd.setDate(0); // last day of monthStart's month
    const spanStart = monthStart < cur ? cur : monthStart;
    const spanEnd = monthEnd > endD ? endD : monthEnd;
    const leftDays = diffDays(start, spanStart.toISOString().split('T')[0]);
    const widthDays = diffDays(spanStart.toISOString().split('T')[0], spanEnd.toISOString().split('T')[0]) + 1;
    const label = spanStart.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const shortLabel = spanStart.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
    spans.push({
      left: Math.max(0, (leftDays / totalDays) * 100),
      width: Math.min(100, (widthDays / totalDays) * 100),
      label, shortLabel,
    });
    monthStart.setMonth(monthStart.getMonth() + 1);
    monthStart.setDate(1);
  }
  return spans;
};

// ── Date ticks (bottom ruler row) — adaptive by range ──
const getDateTicks = (start, end) => {
  const totalDays = diffDays(start, end) + 1;
  const ticks = [];
  const cur = new Date(start + 'T12:00:00');
  const endD = new Date(end + 'T12:00:00');

  // Always emit every single day — every date label is always shown.
  // Enough horizontal space is guaranteed by ganttMinWidth (22px/day minimum).
  while (cur <= endD) {
    const dateStr = cur.toISOString().split('T')[0];
    const offset = diffDays(start, dateStr);
    const dow = cur.getDay();
    const date = cur.getDate();
    const isWeekend = dow === 0 || dow === 6;
    ticks.push({
      dateStr, offset,
      pct: (offset / totalDays) * 100,
      label: date.toString(),
      isWeekend,
      showLabel: true,
    });
    cur.setDate(cur.getDate() + 1);
  }
  return ticks;
};

// ── Weekend column spans for body row shading (≤ 60 days only) ──
const getWeekendColumns = (start, end) => {
  const totalDays = diffDays(start, end) + 1;
  if (totalDays > 60) return [];
  const cols = [];
  const cur = new Date(start + 'T12:00:00');
  const endD = new Date(end + 'T12:00:00');
  while (cur <= endD) {
    const dow = cur.getDay();
    if (dow === 6) {
      const satOffset = diffDays(start, cur.toISOString().split('T')[0]);
      const sunOffset = Math.min(satOffset + 1, totalDays);
      cols.push({ left: (satOffset / totalDays) * 100, width: ((sunOffset - satOffset + 1) / totalDays) * 100 });
    }
    cur.setDate(cur.getDate() + 1);
  }
  return cols;
};

const Avatar = ({ name, size = 36 }) => {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.33, fontWeight: '700', flexShrink: 0, letterSpacing: '0.5px'
    }}>
      {initials}
    </div>
  );
};

const UtilBar = ({ pct, label, sublabel }) => {
  const color = pct > 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#16a34a';
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
          % {label}
        </span>
        <span style={{ fontSize: '14px', fontWeight: '800', color }}>{pct}%</span>
      </div>
      <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: '99px', transition: 'width 0.4s ease' }} />
      </div>
      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>{sublabel}</div>
    </div>
  );
};

const QUICK_RANGES = [
  { label: 'Next 2 weeks', days: 14 },
  { label: 'Next month', days: 30 },
  { label: 'Next 6 weeks', days: 42 },
  { label: 'Next quarter', days: 90 },
];

const ResourceView = () => {
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(addDays(today, 42));
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [dateError, setDateError] = useState('');
  const [tooltip, setTooltip] = useState(null);
  const [summary, setSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);      // { employee } when open
  const [drawerTasks, setDrawerTasks] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerTab, setDrawerTab] = useState('all');

  useEffect(() => {
    employeesAPI.getResourceSummary()
      .then(res => setSummary(res.data))
      .catch(err => console.error('Error fetching resource summary:', err))
      .finally(() => setSummaryLoading(false));
  }, []);

  const totalDays = diffDays(startDate, endDate) + 1;
  const monthSpans = searched ? getMonthSpans(startDate, endDate) : [];
  const dateTicks = searched ? getDateTicks(startDate, endDate) : [];
  const weekendCols = searched ? getWeekendColumns(startDate, endDate) : [];
  // Minimum 22px per day so every date label always fits; label col is 220px
  const ganttMinWidth = Math.max(700, 220 + totalDays * 32);
  const todayOffset = searched ? diffDays(startDate, today) : null;
  const todayPct = todayOffset !== null && todayOffset >= 0 && todayOffset <= totalDays
    ? (todayOffset / totalDays) * 100 : null;

  const freeCount = employees.filter(e => e.is_free).length;
  const occupiedCount = employees.filter(e => !e.is_free).length;

  const fetchData = useCallback(async () => {
    setDateError('');
    if (!startDate || !endDate) { setDateError('Please select both dates.'); return; }
    if (endDate <= startDate) { setDateError('End date must be after start date.'); return; }
    if (totalDays > 365) { setDateError('Date range cannot exceed 1 year.'); return; }
    try {
      setLoading(true);
      const res = await employeesAPI.getResourceView(startDate, endDate);
      setEmployees(res.data);
      setSearched(true);
    } catch (err) {
      setDateError('Failed to load resource data.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, totalDays]);

  const applyQuickRange = (days) => {
    setStartDate(today);
    setEndDate(addDays(today, days));
  };

  const openDrawer = async (emp) => {
    setDrawer(emp);
    setDrawerTab('all');
    setDrawerTasks([]);
    setDrawerLoading(true);
    try {
      const res = await tasksAPI.getByEmployee(emp.employee_id);
      setDrawerTasks(res.data);
    } catch (err) {
      console.error('Error fetching employee tasks:', err);
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawer(null);
    setDrawerTasks([]);
  };

  const getTaskStatus = (task) => {
    if (task.status_id === 5) return 'completed';
    if (task.planned_end < today) return 'slipping';
    if (task.planned_start <= today && task.planned_end >= today) return 'active';
    return 'upcoming';
  };

  const getTodayAllocation = (emp) => {
    // Use backend-computed today_total_alloc (schedule-override-aware)
    if (emp.today_total_alloc !== undefined) return emp.today_total_alloc;
    // Fallback: sum today_allocations if present
    if (emp.today_allocations && emp.today_allocations.length > 0)
      return emp.today_allocations.reduce((s, t) => s + t.allocation_percent, 0);
    return 0;
  };

  const getPeakAllocation = (emp, ticks) => {
    // Use backend-computed daily_allocations (schedule-override-aware)
    const daily = emp.daily_allocations;
    if (daily && Object.keys(daily).length > 0) {
      return Math.max(...Object.values(daily));
    }
    return getTodayAllocation(emp);
  };

  // Returns tasks active on a given dateStr for an employee, with override-aware allocations
  const getDayTasks = (emp, dateStr) => {
    const isToday = dateStr === today;
    // TODAY: use backend-computed today_allocations (already override-aware)
    if (isToday && emp.today_allocations && emp.today_allocations.length > 0) {
      return emp.today_allocations.map(t => ({
        task_id: t.task_id,
        task_name: t.task_name,
        project_name: t.project_name,
        allocation_percent: t.allocation_percent,
        is_slipping: t.is_slipping,
        is_override: t.is_override,
      }));
    }
    // OTHER DAYS: use backend-computed daily_allocations_detail if available.
    // This is schedule-override-aware — if the PM saved a schedule for this day,
    // it reflects exactly what was saved (not raw task defaults).
    if (emp.daily_allocations_detail && emp.daily_allocations_detail[dateStr]) {
      return emp.daily_allocations_detail[dateStr];
    }
    // Fallback: day has zero allocation (daily_allocations_detail not populated for this day)
    // backend Fix #1 now populates detail for every active day, so this path means genuinely empty.
    return [];
  };

  const getBarGeometry = (task) => {
    const effectiveStart = task.forecast_start || task.planned_start;
    const effectiveEnd = (task.forecast_end && task.forecast_end >= task.planned_end) ? task.forecast_end : task.planned_end;
    const clampedStart = effectiveStart < startDate ? startDate : effectiveStart;
    const clampedEnd = effectiveEnd > endDate ? endDate : effectiveEnd;
    const left = (diffDays(startDate, clampedStart) / totalDays) * 100;
    const width = ((diffDays(clampedStart, clampedEnd) + 1) / totalDays) * 100;
    return { left: Math.max(0, left), width: Math.min(100 - Math.max(0, left), width) };
  };

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ fontSize: '26px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Resource View</h2>
            <p style={{ color: '#64748b', marginTop: '4px', fontSize: '14px' }}>Timeline and allocation status for all team members</p>
          </div>
          {searched && (
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#16a34a' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                {freeCount} Available
              </span>
              <span style={{ color: '#cbd5e1' }}>|</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#f59e0b' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />
                {occupiedCount} Occupied
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── RESOURCE SUMMARY ── */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 className="card-title">👥 Resource Summary</h3>
          <span style={{ fontSize: '13px', color: '#7f8c8d' }}>Lifetime stats per team member</span>
        </div>
        {summaryLoading ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>Loading summary…</div>
        ) : summary.length === 0 ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>No employees found.</div>
        ) : (
          <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {summary.map(emp => {
              const hasOverdue = emp.overdue_tasks > 0;
              const onTimeRate = emp.on_time_rate;
              const rateColor = onTimeRate === null ? '#94a3b8' : onTimeRate >= 80 ? '#16a34a' : onTimeRate >= 50 ? '#f59e0b' : '#ef4444';
              return (
                <div
                  key={emp.employee_id}
                  onClick={() => openDrawer(emp)}
                  style={{
                    background: '#fff', borderRadius: '12px',
                    border: `1px solid ${hasOverdue ? '#fca5a5' : '#e2e8f0'}`,
                    borderLeft: `4px solid ${hasOverdue ? '#ef4444' : '#3b82f6'}`,
                    padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px',
                    cursor: 'pointer', transition: 'box-shadow 0.2s, transform 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Avatar name={emp.employee_name} size={36} />
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>{emp.employee_name}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{emp.role || 'Team Member'}{emp.department ? ` · ${emp.department}` : ''}</div>
                      </div>
                    </div>
                    {hasOverdue && (
                      <span style={{
                        background: '#fef2f2', border: '1px solid #fca5a5',
                        color: '#dc2626', borderRadius: '20px', padding: '3px 10px',
                        fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap',
                      }}>
                        ⚠️ {emp.overdue_tasks} overdue
                      </span>
                    )}
                  </div>

                  {/* Top stats row */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {[
                      { label: 'Projects', value: emp.total_projects, icon: '🗂', color: '#3b82f6', bg: '#eff6ff' },
                      { label: 'Tasks', value: emp.total_tasks, icon: '✓', color: '#8b5cf6', bg: '#f5f3ff' },
                      { label: 'Overdue', value: emp.overdue_tasks, icon: '⏰', color: emp.overdue_tasks > 0 ? '#ef4444' : '#16a34a', bg: emp.overdue_tasks > 0 ? '#fef2f2' : '#f0fdf4' },
                    ].map(stat => (
                      <div key={stat.label} style={{ background: stat.bg, borderRadius: '8px', padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: stat.color }}>{stat.value}</div>
                        <div style={{ fontSize: '10px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{stat.icon} {stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Completed tasks breakdown */}
                  {emp.completed_tasks > 0 ? (
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          ✅ Completed Tasks ({emp.completed_tasks})
                        </span>
                        {onTimeRate !== null && (
                          <span style={{ fontSize: '12px', fontWeight: '800', color: rateColor }}>{onTimeRate}% on time</span>
                        )}
                      </div>
                      {/* On-time rate bar */}
                      {onTimeRate !== null && (
                        <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden', marginBottom: '8px' }}>
                          <div style={{ height: '100%', width: `${onTimeRate}%`, background: rateColor, borderRadius: '99px', transition: 'width 0.4s ease' }} />
                        </div>
                      )}
                      {/* On time / Late / No actual breakdown */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {emp.completed_on_time > 0 && (
                          <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: '600' }}>
                            ✅ {emp.completed_on_time} on time
                          </span>
                        )}
                        {emp.completed_late > 0 && (
                          <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: '600' }}>
                            🔴 {emp.completed_late} late
                          </span>
                        )}
                        {emp.completed_no_actual > 0 && (
                          <span style={{ background: '#f1f5f9', color: '#64748b', borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: '600' }}>
                            ⬜ {emp.completed_no_actual} no date recorded
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
                      No completed tasks yet
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Configure Timeline */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 className="card-title">Configure Timeline</h3>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
          <div className="form-group" style={{ margin: 0, flex: '1', minWidth: '150px' }}>
            <label className="form-label">From</label>
            <input type="date" className="form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0, flex: '1', minWidth: '150px' }}>
            <label className="form-label">To</label>
            <input type="date" className="form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={fetchData} disabled={loading} style={{ height: '46px', minWidth: '140px' }}>
            {loading ? '⏳ Loading...' : '📊 View Resources'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {QUICK_RANGES.map(r => (
            <button key={r.label} onClick={() => applyQuickRange(r.days)} style={{
              padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: '20px',
              fontSize: '12px', fontWeight: '500', background: '#f8fafc', color: '#475569', cursor: 'pointer',
            }}
              onMouseEnter={e => { e.target.style.background = '#e2e8f0'; }}
              onMouseLeave={e => { e.target.style.background = '#f8fafc'; }}
            >{r.label}</button>
          ))}
        </div>
        {dateError && (
          <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', borderLeft: '4px solid #e74c3c', color: '#991b1b', fontSize: '14px' }}>
            ❌ {dateError}
          </div>
        )}
      </div>

      {!searched && !loading && (
        <div className="empty-state" style={{ marginTop: '40px' }}>
          <div className="empty-state-icon">👥</div>
          <div className="empty-state-text">Select a date range and click View Resources</div>
        </div>
      )}

      {searched && employees.length > 0 && (
        <>
          {/* ── GANTT ── */}
          <p style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 12px 0' }}>Allocation Timeline</p>

          {/* Gantt wrapper — position:relative scopes the sticky header so it unsticks when this container leaves viewport */}
          <div style={{ position: 'relative', marginBottom: '36px' }}>

          {/* Sticky Gantt header — scoped to wrapper above, unsticks naturally when Gantt scrolls away */}
          <div style={{
            position: 'sticky', top: 0, zIndex: 20,
            background: '#fff',
            borderRadius: '14px 14px 0 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9',
            borderBottom: 'none',
            marginBottom: 0,
          }}>
            <div style={{ padding: '14px 24px 10px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontWeight: '700', fontSize: '15px', color: '#1e293b' }}>Resource Timeline</div>
              <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '2px' }}>
                {formatShortDate(startDate)} – {formatShortDate(endDate)} {new Date(endDate + 'T12:00:00').getFullYear()}
              </div>
            </div>
            {/* Date ruler — hidden scrollbar, synced with body */}
            <div
              id="gantt-header-scroll"
              style={{ overflowX: 'auto', overflowY: 'hidden', msOverflowStyle: 'none', scrollbarWidth: 'none' }}
              onScroll={e => {
                const body = document.getElementById('gantt-body-scroll');
                if (body && body.scrollLeft !== e.target.scrollLeft) body.scrollLeft = e.target.scrollLeft;
              }}
            >
              <style>{`#gantt-header-scroll::-webkit-scrollbar { display: none; }`}</style>
              <div style={{ minWidth: `${ganttMinWidth}px` }}>

                {/* ── Month header row ── */}
                <div style={{ display: 'flex', paddingLeft: '220px', paddingRight: '16px' }}>
                  <div style={{ position: 'relative', flex: 1, height: '26px', borderBottom: '1px solid #e2e8f0' }}>
                    {monthSpans.map((span, i) => (
                      <div key={i} style={{
                        position: 'absolute',
                        left: `${span.left}%`,
                        width: `${span.width}%`,
                        top: 0, height: '26px',
                        display: 'flex', alignItems: 'center',
                        paddingLeft: '8px',
                        borderLeft: i > 0 ? '1px solid #e2e8f0' : 'none',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                      }}>
                        <span style={{
                          fontSize: '11px', fontWeight: '700',
                          color: '#475569', textTransform: 'uppercase',
                          letterSpacing: '0.5px', whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {span.width < 8 ? span.shortLabel : span.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Date ticks sub-row ── */}
                <div style={{ display: 'flex', paddingLeft: '220px', paddingRight: '16px', position: 'relative' }}>
                  {/* Weekend shading */}
                  {weekendCols.map((col, i) => (
                    <div key={`hdr-wknd-${i}`} style={{
                      position: 'absolute',
                      left: `calc(220px + (100% - 236px) * ${col.left / 100})`,
                      width: `calc((100% - 236px) * ${col.width / 100})`,
                      top: 0, bottom: 0, background: '#f8fafc', pointerEvents: 'none',
                    }} />
                  ))}
                  <div style={{ position: 'relative', flex: 1, height: '24px' }}>
                    {dateTicks.map(tick => (
                      <React.Fragment key={tick.dateStr}>
                        {/* Vertical grid line for every day */}
                        <div style={{
                          position: 'absolute', left: `${tick.pct}%`,
                          top: 0, bottom: 0, width: '1px',
                          background: tick.isWeekend ? '#e8edf2' : '#f1f5f9',
                          pointerEvents: 'none',
                        }} />
                        {/* Label — only shown when there's enough space */}
                        {tick.showLabel && (
                          <span style={{
                            position: 'absolute',
                            left: `${tick.pct + (1 / totalDays) * 50}%`,
                            top: '4px',
                            fontSize: '11px',
                            color: tick.isWeekend ? '#cbd5e1' : '#94a3b8',
                            fontWeight: tick.isWeekend ? '400' : '500',
                            transform: 'translateX(-50%)',
                            whiteSpace: 'nowrap',
                            lineHeight: 1,
                          }}>{tick.label}</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Gantt body card — rounded bottom only, no top radius since header sits above */}
          <div style={{ background: '#fff', borderRadius: '0 0 14px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', borderTop: 'none' }}>

            {/* Scrollable body */}
            <div
              id="gantt-body-scroll"
              style={{ overflowX: 'auto' }}
              onScroll={e => {
                const header = document.getElementById('gantt-header-scroll');
                if (header && header.scrollLeft !== e.target.scrollLeft) header.scrollLeft = e.target.scrollLeft;
              }}
            >
              <div style={{ minWidth: `${ganttMinWidth}px` }}>
                {/* Rows */}
                {employees.map((emp, empIdx) => {
                    return (
                    <div key={emp.employee_id} style={{
                      display: 'flex', alignItems: 'stretch',
                      borderBottom: empIdx < employees.length - 1 ? '1px solid #f8fafc' : 'none',
                      background: empIdx % 2 === 0 ? '#fff' : '#fafbfc',
                      minHeight: '44px',
                      paddingRight: '16px',
                    }}>
                      {/* Label */}
                      <div style={{ width: '220px', flexShrink: 0, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Avatar name={emp.employee_name} size={34} />
                        <div style={{ overflow: 'hidden' }}>
                          <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.employee_name}</div>
                          {emp.role && <div style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.role}</div>}
                        </div>
                      </div>

                      {/* Heatmap area — one cell per calendar day */}
                      <div style={{ flex: 1, display: 'flex', alignItems: 'stretch', position: 'relative', minHeight: '52px' }}>
                        {dateTicks.map((tick, i) => {
                          const nextTick = dateTicks[i + 1];
                          const cellWidth = nextTick
                            ? nextTick.pct - tick.pct
                            : (1 / totalDays) * 100;
                          const isWeekend = tick.isWeekend;
                          const isToday   = tick.dateStr === today;
                          const alloc     = emp.is_free ? 0 : ((emp.daily_allocations || {})[tick.dateStr] || 0);

                          // Cell colour based on utilisation
                          let bg, textColor;
                          if (isWeekend) {
                            bg = '#f8fafc'; textColor = 'transparent';
                          } else if (alloc === 0) {
                            bg = '#f0fdf4'; textColor = '#86efac';          // 0% — green
                          } else if (alloc <= 50) {
                            bg = '#fef3c7'; textColor = '#92400e';           // 1–50% — amber
                          } else if (alloc <= 80) {
                            bg = '#fecaca'; textColor = '#991b1b';           // 51–80% — light red
                          } else if (alloc <= 120) {
                            bg = '#ef4444'; textColor = '#fff';              // 81–120% — red
                          } else {
                            bg = '#b91c1c'; textColor = '#fff';              // >120% — bright red
                          }

                          // Tooltip data resolved on hover via getDayTasks — see onMouseEnter below

                          return (
                            <div
                              key={tick.dateStr}
                              style={{
                                width: `${cellWidth}%`,
                                background: bg,
                                borderRight: isToday ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.6)',
                                borderLeft: isToday ? '2px solid #3b82f6' : undefined,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: alloc > 0 ? 'pointer' : 'default',
                                transition: 'filter 0.1s',
                                position: 'relative',
                                flexShrink: 0,
                              }}
                              onMouseEnter={(e) => {
                                if (!isWeekend) {
                                  e.currentTarget.style.filter = 'brightness(0.92)';
                                  const isToday = tick.dateStr === today;
                                  const dayTasks = isToday && emp.today_allocations && emp.today_allocations.length > 0
                                    ? emp.today_allocations
                                    : getDayTasks(emp, tick.dateStr);
                                  const dayTotal = isToday && emp.today_total_alloc !== undefined
                                    ? emp.today_total_alloc
                                    : alloc;
                                  setTooltip({
                                    x: e.clientX, y: e.clientY,
                                    dateStr: tick.dateStr,
                                    dateAlloc: dayTotal,
                                    empName: emp.employee_name,
                                    dayTasks,
                                  });
                                }
                              }}
                              onMouseMove={(e) => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                              onMouseLeave={(e) => { e.currentTarget.style.filter = ''; setTooltip(null); }}
                            >
                              {!isWeekend && alloc > 0 && cellWidth > 2.5 && (
                                <span style={{ fontSize: '9px', fontWeight: '700', color: textColor, userSelect: 'none', pointerEvents: 'none', overflow: 'hidden', maxWidth: '100%', textAlign: 'center' }}>
                                  {alloc > 99 ? `${alloc}` : `${alloc}%`}
                                </span>
                              )}
                            </div>
                          );
                        })}
                        {/* Today indicator line on top */}
                        {todayPct !== null && (
                          <div style={{ position: 'absolute', left: `${todayPct}%`, top: 0, bottom: 0, width: '2px', background: '#3b82f6', opacity: 0.9, zIndex: 5, pointerEvents: 'none' }} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div style={{ padding: '12px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Utilisation:</span>
                {[
                  { bg: '#f0fdf4', border: '1px solid #bbf7d0', label: '0% free' },
                  { bg: '#fef3c7', border: '1px solid #fcd34d', label: '1–50%' },
                  { bg: '#fecaca', border: '1px solid #f87171', label: '51–80%' },
                  { bg: '#ef4444', label: '81–120%' },
                  { bg: '#b91c1c', label: '> 120%' },
                  { bg: '#f8fafc', border: '1px solid #e2e8f0', label: 'Weekend' },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748b' }}>
                    <div style={{ width: '20px', height: '14px', background: item.bg, border: item.border, borderRadius: '3px' }} />
                    {item.label}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748b' }}>
                <div style={{ width: '3px', height: '14px', background: '#3b82f6', borderRadius: '2px' }} />
                Today
              </div>
            </div>
          </div>
          </div>{/* end Gantt wrapper */}

        </>
      )}

      {/* ── HEATMAP DAY TOOLTIP ── */}
      {tooltip && (
        <div style={{
          position: 'fixed',
          left: tooltip.x - 14,
          top: tooltip.y - 10,
          zIndex: 300,
          background: '#1e293b',
          color: '#f8fafc',
          borderRadius: '10px',
          padding: '12px 14px',
          minWidth: '200px',
          maxWidth: '280px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
          pointerEvents: 'none',
          fontSize: '12px',
        }}>
          {/* Date header */}
          <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '8px', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '6px' }}>
            {new Date(tooltip.dateStr + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
            {tooltip.dateStr === today && <span style={{ marginLeft: '6px', fontSize: '10px', background: '#3b82f6', borderRadius: '4px', padding: '1px 5px' }}>TODAY</span>}
          </div>
          {tooltip.dayTasks.length === 0 ? (
            <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>No tasks scheduled</div>
          ) : (
            <>
              {tooltip.dayTasks.map((t, i) => (
                <div key={t.task_id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden' }}>
                    {t.is_slipping && <span style={{ color: '#fca5a5', flexShrink: 0 }}>⚠</span>}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: t.is_slipping ? '#fca5a5' : '#e2e8f0' }}>{t.task_name}</span>
                    {t.is_override && <span style={{ fontSize: '9px', color: '#a78bfa', flexShrink: 0 }}>override</span>}
                  </div>
                  <span style={{ fontWeight: '700', flexShrink: 0, color: t.allocation_percent > 100 ? '#fca5a5' : t.allocation_percent >= 80 ? '#fcd34d' : '#6ee7b7' }}>
                    {t.allocation_percent}%
                  </span>
                </div>
              ))}
              {/* Total bar */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '6px', paddingTop: '6px', display: 'flex', justifyContent: 'space-between', fontWeight: '700' }}>
                <span style={{ color: '#94a3b8' }}>Total</span>
                {(() => {
                  const sum = tooltip.dayTasks.reduce((s, t) => s + t.allocation_percent, 0);
                  return <span style={{ color: sum > 100 ? '#fca5a5' : '#6ee7b7' }}>{sum}%</span>;
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── EMPLOYEE TASK DRAWER ── */}
      {drawer && (
        <>
          {/* Backdrop */}
          <div
            onClick={closeDrawer}
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, backdropFilter: 'blur(2px)' }}
          />
          {/* Panel */}
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: '460px', maxWidth: '95vw',
            background: '#fff', zIndex: 201, boxShadow: '-8px 0 40px rgba(0,0,0,0.18)',
            display: 'flex', flexDirection: 'column', overflowY: 'auto',
          }}>
            {/* Header */}
            <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <Avatar name={drawer.employee_name} size={44} />
                <div>
                  <div style={{ fontWeight: '700', fontSize: '17px', color: '#1e293b' }}>{drawer.employee_name}</div>
                  <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '2px' }}>{drawer.role || 'Team Member'}</div>
                </div>
              </div>
              <button onClick={closeDrawer} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94a3b8', lineHeight: 1, padding: '4px', marginTop: '-2px' }}>✕</button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #f1f5f9', padding: '0 24px' }}>
              {[
                { key: 'all',       label: 'All Tasks' },
                { key: 'active',    label: 'Active' },
                { key: 'slipping',  label: '⚠ Slipping' },
                { key: 'upcoming',  label: 'Upcoming' },
                { key: 'completed', label: 'Completed' },
              ].map(tab => {
                const count = tab.key === 'all'
                  ? drawerTasks.length
                  : drawerTasks.filter(t => getTaskStatus(t) === tab.key).length;
                if (tab.key !== 'all' && count === 0) return null;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setDrawerTab(tab.key)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '10px 14px', fontSize: '12px', fontWeight: drawerTab === tab.key ? '700' : '500',
                      color: drawerTab === tab.key ? '#3b82f6' : '#64748b',
                      borderBottom: drawerTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
                      marginBottom: '-1px', whiteSpace: 'nowrap',
                    }}
                  >
                    {tab.label} {count > 0 && <span style={{ background: drawerTab === tab.key ? '#eff6ff' : '#f1f5f9', color: drawerTab === tab.key ? '#3b82f6' : '#94a3b8', borderRadius: '99px', padding: '1px 6px', fontSize: '10px', fontWeight: '700', marginLeft: '4px' }}>{count}</span>}
                  </button>
                );
              })}
            </div>

            {/* Task list */}
            <div style={{ flex: 1, padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {drawerLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '14px' }}>Loading tasks…</div>
              ) : (() => {
                const filtered = drawerTab === 'all' ? drawerTasks : drawerTasks.filter(t => getTaskStatus(t) === drawerTab);
                if (filtered.length === 0) return (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '14px' }}>No tasks in this category</div>
                );
                return filtered.map(task => {
                  const status = getTaskStatus(task);
                  const isSlipping = status === 'slipping';
                  const isCompleted = status === 'completed';
                  const alloc = task.allocation_percent || 0;
                  const allocColor = isSlipping ? '#ef4444' : alloc >= 100 ? '#f59e0b' : alloc >= 80 ? '#3b82f6' : '#10b981';
                  const statusBg = isSlipping ? '#fef2f2' : isCompleted ? '#f8fafc' : status === 'active' ? '#eff6ff' : '#fafafa';
                  const statusBorder = isSlipping ? '#fca5a5' : isCompleted ? '#e2e8f0' : status === 'active' ? '#bfdbfe' : '#e2e8f0';
                  return (
                    <div key={task.task_id} style={{ background: statusBg, border: `1px solid ${statusBorder}`, borderRadius: '10px', padding: '14px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: isSlipping ? '#dc2626' : '#1e293b' }}>
                          {isSlipping ? '⚠️ ' : isCompleted ? '✅ ' : '🕐 '}{task.task_name}
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: allocColor, flexShrink: 0 }}>{alloc}%</span>
                      </div>
                      <div style={{ fontSize: '12px', color: isSlipping ? '#ef4444' : '#94a3b8', fontWeight: isSlipping ? '600' : '400', marginBottom: '6px' }}>
                        {isSlipping
                          ? `⚠ ${diffDays(task.planned_end, today)}d overdue · planned ${formatShortDate(task.planned_start)} – ${formatShortDate(task.planned_end)}`
                          : `${formatShortDate(task.planned_start)} – ${formatShortDate(task.planned_end)}`
                        }
                        {task.forecast_end && isSlipping && (
                          <span style={{ marginLeft: '8px', color: '#f59e0b', fontWeight: '600' }}>· forecast {formatShortDate(task.forecast_end)}</span>
                        )}
                      </div>
                      {isCompleted && (() => {
                        const late = task.task_category === 'completed_late' || (task.actual_end && task.actual_end > task.planned_end);
                        const daysLate = late && task.actual_end ? diffDays(task.planned_end, task.actual_end) : 0;
                        return (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', borderRadius: '6px', padding: '3px 8px', marginBottom: '6px', fontSize: '11px', fontWeight: '600', background: late ? '#fef2f2' : '#f0fdf4', color: late ? '#dc2626' : '#16a34a', border: `1px solid ${late ? '#fca5a5' : '#bbf7d0'}` }}>
                            {late ? `⚠ Completed ${daysLate}d late · actual end ${formatShortDate(task.actual_end)}` : `✓ Completed on time · ${formatShortDate(task.actual_end)}`}
                          </div>
                        );
                      })()}
                      {task.project_name && (
                        <div style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', display: 'inline-block', borderRadius: '4px', padding: '2px 8px' }}>{task.project_name}</div>
                      )}
                      {/* Allocation bar */}
                      <div style={{ height: '3px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden', marginTop: '10px' }}>
                        <div style={{ height: '100%', width: `${Math.min(alloc, 100)}%`, background: allocColor, borderRadius: '99px' }} />
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ResourceView;
